import { defineStore } from "pinia";
import { computed } from "vue";
import { useAuth } from "@/composables/useAuth";
import { getRuntimeConfig } from "@/lib/runtime-config";
import { local, session } from "@/lib/storage";

export const useAuthStore = defineStore("auth", () => {
  const a = useAuth();

  const isAllowedDomain = computed(() => {
    if (!a.email.value) return false;
    const allowed = getRuntimeConfig().allowedEmailDomain.toLowerCase();
    return a.email.value.toLowerCase().endsWith(`@${allowed}`);
  });

  async function init(): Promise<void> {
    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const state = url.searchParams.get("state");
    const expected = session.get("oauth_state");
    const verifier = session.get("pkce_verifier");
    console.log("[auth.init] start", {
      url: window.location.href,
      hasCode: !!code,
      codeLen: code?.length,
      state,
      expectedState: expected,
      hasVerifier: !!verifier,
      verifierLen: verifier?.length,
      hasRefreshToken: !!local.get("refresh_token"),
    });

    if (code) {
      if (expected && state && expected !== state) {
        console.error("[auth.init] state mismatch", { expected, got: state });
        throw new Error("OAuth state mismatch — possible tampered redirect");
      }
      try {
        console.log("[auth.init] exchanging code…");
        await a.exchangeCode(code);
        console.log("[auth.init] exchange OK", {
          isSignedIn: a.isSignedIn.value,
          email: a.email.value,
          hasIdToken: !!a.idToken.value,
        });
      } catch (err) {
        console.error("[auth.init] exchange threw", err);
        throw err;
      } finally {
        const clean = new URL(window.location.href);
        clean.search = "";
        history.replaceState({}, "", clean.toString());
        console.log("[auth.init] URL cleaned to", clean.toString());
      }
    } else if (local.get("refresh_token")) {
      try {
        console.log("[auth.init] refreshing from stored refresh_token…");
        await a.refresh();
        console.log("[auth.init] refresh OK", { isSignedIn: a.isSignedIn.value });
      } catch (err) {
        console.warn("[auth.init] refresh failed, falling through signed-out", err);
      }
    } else {
      console.log("[auth.init] no code, no refresh_token — signed-out");
    }
  }

  return {
    // state — proxied refs so consumers can use `store.isSignedIn` reactively
    idToken: a.idToken,
    isSignedIn: a.isSignedIn,
    email: a.email,
    role: a.role,
    isAdmin: a.isAdmin,
    projects: a.projects,
    canSeeProject: a.canSeeProject,
    groups: a.groups,
    claims: a.claims,
    isAllowedDomain,
    // actions
    init,
    startSignIn: a.startSignIn,
    signOut: a.signOut,
    ensureFreshToken: a.ensureFreshToken,
  };
});
